// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Button,
  Description,
  InlineAlert,
  InputCheckbox,
  LgPadding,
  Link,
  MdSpacing,
  XsSpacing,
} from '@alto/design-system';
import {
  type AttestationRequestItem,
  type AttestationRequestOption,
} from '@alto/scriptdash/alto/patient_app/attestations/v1/attestation_requests_endpoint';
import React, { useContext, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { CouponDetailsActionSheet } from '../../coupons/components/CouponDetailsActionSheet';
import { TermsWebView } from '../../shared/components/TermsWebView';
import { REVIEW_TERMS_FOOTNOTE } from '../../shared/components/termsWebViewConstants';
import { useCreateAttestation } from '../queries/queries';

type Props = {
  attestationRequest: AttestationRequestItem;
  onDismiss: () => void;
  onSuccess: () => void;
};

const SUBMIT_ERROR_TITLE = 'Could not record attestation';
const SUBMIT_ERROR_FALLBACK = 'Please try again or contact support.';

const styles = StyleSheet.create({
  contentWrapper: { flexGrow: 0 },
  scrollContentCompact: { flexGrow: 0 },
});

const sortOptionsByDisplayOrder = (options: AttestationRequestOption[]) =>
  [...options].sort((a, b) => a.display_order - b.display_order);

const getAcceptedOption = (options: AttestationRequestOption[]) =>
  options.find((option) => option.accepted) ?? options[0];

export const AttestationActionSheet = ({ attestationRequest, onDismiss, onSuccess }: Props) => {
  const { goToPreviousActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);
  const { mutateAsync, isPending } = useCreateAttestation();
  const { trackEvent } = useAnalytics();

  const { program_config: programConfig, statement } = attestationRequest;
  const sortedOptions = sortOptionsByDisplayOrder(statement.options);
  const acceptedOption = getAcceptedOption(sortedOptions);

  const [hasAccepted, setHasAccepted] = useState(false);
  const [isAcceptDisabled, setIsAcceptDisabled] = useState(true);
  const [submitError, setSubmitError] = useState('');

  const showHowItWorksLink = programConfig.offer_description || programConfig.offer_details;

  const trackAttestationDismissed = () => {
    trackEvent({
      event: EVENTS.ATTESTATION_DISMISSED,
      params: {
        billable_type: programConfig.billable_type,
        attestation_type: statement.unique_identifier,
      },
    });
  };

  const handleHowItWorksPress = () => {
    if (!showHowItWorksLink) {
      return;
    }

    setActiveActionSheet(
      <CouponDetailsActionSheet
        description={programConfig.offer_description}
        details={programConfig.offer_details}
      />,
      { shouldRetainPreviousActionSheet: true },
    );
  };

  const handleDismiss = () => {
    trackAttestationDismissed();
    onDismiss();
    goToPreviousActionSheet();
  };

  const handleBackFromConsent = () => {
    trackAttestationDismissed();
    onDismiss();
  };

  const handleConfirm = async () => {
    if (!hasAccepted || !acceptedOption) {
      return;
    }

    setSubmitError('');

    try {
      await mutateAsync({
        attestation_type: statement.unique_identifier,
        election_name: acceptedOption.election_name,
      });

      onSuccess();
      goToPreviousActionSheet();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : SUBMIT_ERROR_FALLBACK);
    }
  };

  return (
    <ActionSheetV2
      analyticsName="attestation_action_sheet"
      contentContainerStyle={styles.scrollContentCompact}
      onBack={handleBackFromConsent}
      title={programConfig.terms_title ?? undefined}
      fullscreen
      checkbox={
        <InputCheckbox
          checkPlacement="left"
          label=""
          name="acceptAttestationTerms"
          descriptions={
            <Description color={isAcceptDisabled ? COLORS.TEXT_COLORS.DISABLED : COLORS.TEXT_COLORS.GREY}>
              {acceptedOption?.description}
            </Description>
          }
          disabled={isAcceptDisabled}
          fullBleed
          noHighlight
          onPress={() => {
            setHasAccepted(!hasAccepted);
            setSubmitError('');
          }}
          testID="accept-attestation-terms"
          value={hasAccepted}
        />
      }
      footnote={
        <>
          {submitError ? (
            <>
              <InlineAlert type="error">
                <Description fontFamily="bold">{SUBMIT_ERROR_TITLE}</Description>
                <XsSpacing />
                <Description>{submitError}</Description>
              </InlineAlert>
              <MdSpacing />
            </>
          ) : null}
          <Description
            color={COLORS.TEXT_COLORS.GREY}
            fontFamily="semibold"
          >
            {REVIEW_TERMS_FOOTNOTE}
          </Description>
        </>
      }
      buttons={[
        <Button
          disabled={!hasAccepted || isPending}
          key="attestation-confirm-button"
          label={programConfig.patient_cta_text}
          loading={isPending}
          loadingLabel="Submitting"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onPress={handleConfirm}
        />,
        <Button
          key="attestation-cancel-button"
          label="Cancel"
          onPress={handleDismiss}
          type="tertiary"
        />,
      ]}
    >
      <View
        style={styles.contentWrapper}
        testID="attestation-action-sheet-content"
      >
        <LgPadding topPadding={SPACING.STATIC.NONE}>
          {showHowItWorksLink ? (
            <>
              <Link onPress={handleHowItWorksPress}>How it works</Link>
              <XsSpacing />
            </>
          ) : null}
          <TermsWebView
            htmlContent={statement.terms}
            onScrolledToBottom={() => {
              setIsAcceptDisabled(false);
            }}
          />
        </LgPadding>
      </View>
    </ActionSheetV2>
  );
};
